<script setup lang="ts">
    import { inject, ref, watch } from 'vue';
    import FormInput from '@shared/components/Form/FormInput.vue';
    import FormCheckable from '@shared/components/Form/FormCheckable.vue';
    import InfoPopoverButton from '@shared/components/Form/InfoPopoverButton.vue';
    import MultiSelect from '@shared/components/MultiSelect/MultiSelect.vue';
    import FeatureLock from '@shared/components/FeatureLock.vue';
    import BSDropdownButton from '@shared/components/BSDropdownButton.vue';
    import { SwitchStyle } from '@shared/components/Form/Form';
    import { setupTags } from '@shared/components/Tags/Tags';
    import { type Event, type Tag, EventStatusNames } from '@views/Event/EventTypes';
    import { eventKey, saveEventPropertyIfChangedKey, saveEventPropertyKey, saveEventTagsKey, canEditKey, canWriteKey } from '@views/Event/EventEditor/event-editor-keys';

    const event = inject(eventKey)!;
    const canEdit = inject(canEditKey)!;
    const canWrite = inject(canWriteKey)!;
    const saveEventProperty = inject(saveEventPropertyKey)!;
    const saveEventPropertyIfChanged = inject(saveEventPropertyIfChangedKey)!;
    const saveEventTags = inject(saveEventTagsKey)!;

    const tagsCatalog = setupTags();

    const description = ref('');
    const tags = ref<Tag[]>([]);
    const languageId = ref('sv');
    const requireAttestation = ref(false);
    const useRelativeSchedule = ref(false);

    watch(
        () => event.value?.Description,
        (value) => (description.value = value ?? ''),
        { immediate: true }
    );
    watch(
        () => event.value?.Tags,
        (value) => (tags.value = value ?? []),
        { immediate: true }
    );
    watch(
        () => event.value?.LanguageId,
        (value) => (languageId.value = value ?? 'sv'),
        { immediate: true }
    );
    watch(
        () => event.value?.RequireAttestation,
        (value) => (requireAttestation.value = value ?? false),
        { immediate: true }
    );
    watch(
        () => event.value?.UseRelativeSchedule,
        (value) => (useRelativeSchedule.value = value ?? false),
        { immediate: true }
    );

    const scheduleLockedInfo = (status: EventStatusNames | undefined) => canEdit.value && status === EventStatusNames.Active;

    function onTagsChange(newItems: Tag[]) {
        tags.value = newItems;
        saveEventTags(newItems);
    }

    async function onNewTag(title: string) {
        await tagsCatalog.create(title, tags.value);
        saveEventTags(tags.value);
    }

    type Language = { id: string; label: string; icon: string; culture: string };
    const languages: Language[] = [
        { id: 'sv', label: $Resources.LangSvLabel, icon: 'se', culture: 'sv-SE' },
        { id: 'en', label: $Resources.LangEnLabel, icon: 'us', culture: 'en-US' },
        { id: 'uk', label: $Resources.LangUkLabel, icon: 'gb', culture: 'en-GB' },
        { id: 'fr', label: $Resources.LangFrLabel, icon: 'fr', culture: 'fr-FR' },
        { id: 'no', label: $Resources.LangNoLabel, icon: 'no', culture: 'no-NO' },
        { id: 'fi', label: $Resources.LangFiLabel, icon: 'fi', culture: 'fi-FI' },
        { id: 'da', label: $Resources.LangDaLabel, icon: 'dk', culture: 'da-DK' },
    ];
    const currentLanguage = () => languages.find((lang) => lang.id === languageId.value) ?? languages[0];

    async function selectLanguage(language: Language) {
        languageId.value = language.id;
        const saved = await Promise.all([
            saveEventProperty('LanguageId', language.id, $Resources.EventLanguageLabel),
            saveEventProperty('Culture', language.culture, $Resources.EventLanguageLabel),
        ]);
        // Event-language resources are loaded on page load, so reload to apply them (same as the old KO editor).
        // Only when both saves succeeded, so a failed save keeps its error alert visible.
        if (saved.every(Boolean)) {
            window.location.reload();
        }
    }

    function onRequireAttestationChange(newValue: boolean) {
        requireAttestation.value = newValue;
        saveEventProperty('RequireAttestation', String(newValue), $Resources.RequireAttestationLabel);
    }

    function onUseRelativeScheduleChange(newValue: boolean) {
        useRelativeSchedule.value = newValue;
        saveEventProperty('UseRelativeSchedule', String(newValue), $Resources.UseRelativeScheduleLabel);
    }
</script>

<template>
    <div v-if="event" class="max-700">
        <h2 class="h3 m-0-t">{{ $Resources.SettingsTab }}</h2>

        <FormInput
            class="form-group"
            :label="$Resources.EventInternalDescriptionLabel"
            v-model="description"
            :placeholder="$Resources.EventDescriptionPlaceholder"
            :disabled="!canWrite"
            @onBlur="saveEventPropertyIfChanged('Description', description, $Resources.EventDescriptionLabel)" />

        <MultiSelect
            class="form-group"
            :selected-items="tags"
            :items="tagsCatalog.state.list"
            :loading="!tagsCatalog.state.loaded"
            select-prop="TagId"
            display-prop="Title"
            :label="$Resources.TagsLabel"
            :info-text="$Resources.TagsHelpText"
            :disabled="!canWrite"
            @update:selectedItems="onTagsChange"
            @newItem="onNewTag" />

        <div class="m-lg-b">
            <h3 class="h5">
                {{ $Resources.EventLanguageLabel }}:
                <InfoPopoverButton :heading="$Resources.EventLanguageLabel" :info-text="$Resources.EventLanguageHelpText" />
            </h3>

            <BSDropdownButton :btnClass="canEdit ? '' : 'pe-none'" :closeOnClickInside="true">
                <template #toggle>
                    <span class="flag-icon" :class="'flag-icon-' + currentLanguage().icon"></span>
                    {{ currentLanguage().label }}
                    <span v-if="canEdit" class="caret m-sm-l"></span>
                </template>
                <template #content>
                    <template v-if="canEdit">
                        <li v-for="language in languages" :key="language.id" :class="{ active: language.id === languageId }">
                            <a href="javascript:void(0)" @click="selectLanguage(language)">
                                <span class="flag-icon" :class="'flag-icon-' + language.icon"></span>
                                {{ language.label }}
                            </a>
                        </li>
                    </template>
                </template>
            </BSDropdownButton>
        </div>

        <FeatureLock
            feature="Events.RelativeSchedule"
            showUpgradeAlert
            :infoTitle="$Resources.UseRelativeScheduleLabel"
            :infoText="$Resources.UseRelativeScheduleInfoText">
            <div class="form-group">
                <FormCheckable
                    :label="
                        scheduleLockedInfo(event.Status)
                            ? $Resources.UseRelativeScheduleLabel + ' (' + $Resources.EventMustBeUnpublishedToEditInfo + ')'
                            : $Resources.UseRelativeScheduleLabel
                    "
                    :switchStyle="SwitchStyle.Standard"
                    :checked="useRelativeSchedule"
                    :disabled="!canEdit || event.Status === EventStatusNames.Active"
                    :infoTextHeading="$Resources.UseRelativeScheduleLabel"
                    :infoText="$Resources.UseRelativeScheduleInfoText"
                    @update:modelValue="onUseRelativeScheduleChange" />
            </div>
        </FeatureLock>

        <FeatureLock
            feature="Event.Attestation"
            showUpgradeAlert
            :infoTitle="$Resources.RequireAttestationFeatureTitle"
            :infoText="$Resources.RequireAttestationInfoText">
            <div class="form-group">
                <FormCheckable
                    :label="
                        scheduleLockedInfo(event.Status)
                            ? $Resources.RequireAttestationLabel + ' (' + $Resources.EventMustBeUnpublishedToEditInfo + ')'
                            : $Resources.RequireAttestationLabel
                    "
                    :switchStyle="SwitchStyle.Standard"
                    :checked="requireAttestation"
                    :disabled="!canEdit || event.Status === EventStatusNames.Active"
                    :infoTextHeading="$Resources.RequireAttestationFeatureTitle"
                    :infoText="$Resources.RequireAttestationInfoText"
                    @update:modelValue="onRequireAttestationChange" />
            </div>
        </FeatureLock>
    </div>
</template>
